import React from "react";
import { Link } from "react-router-dom";
import { HeartHandshake, Phone, Mail, MapPin } from "lucide-react";
import { CONTACT_PHONE, CONTACT_PHONE_HREF, CONTACT_EMAIL, CONTACT_EMAIL_HREF } from "@/components/Navbar";

export default function Footer() {
  return (
    <footer className="bg-sage-900 text-stone2-100 mt-16" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-14 grid md:grid-cols-12 gap-10">
        {/* Brand + tagline */}
        <div className="md:col-span-4">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-ochre-500 text-white">
              <HeartHandshake className="w-5 h-5" />
            </span>
            <span className="font-heading font-bold text-lg text-white">
              Autism<span className="text-ochre-400">Babysitters</span>
            </span>
          </Link>
          <p className="mt-4 text-sm text-stone2-300 leading-relaxed max-w-sm">
            A calmer way to find care for families supporting individuals on the spectrum.
            Vetted, trained, and matched with your child in mind.
          </p>
        </div>

        {/* Contact — prominent */}
        <div className="md:col-span-5" data-testid="footer-contact">
          <span className="text-xs uppercase tracking-[0.24em] text-ochre-400 font-semibold">Talk to us</span>
          <h3 className="mt-3 font-heading font-bold text-2xl text-white leading-tight">
            Need help finding someone in your area?
          </h3>
          <p className="mt-2 text-sm text-stone2-300">
            We can help you. Contact us today — a real person will match you with the right sitter.
          </p>

          <div className="mt-5 space-y-3">
            <a
              href={CONTACT_PHONE_HREF}
              data-testid="footer-phone"
              className="group flex items-center gap-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-ochre-400/60 px-4 py-3 transition-colors duration-200"
            >
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-ochre-500 text-white">
                <Phone className="w-4 h-4" />
              </span>
              <span className="flex-1">
                <span className="block text-xs uppercase tracking-[0.2em] text-stone2-400 font-semibold">Call</span>
                <span className="block text-lg font-heading font-bold text-white">{CONTACT_PHONE}</span>
              </span>
            </a>
            <a
              href={CONTACT_EMAIL_HREF}
              data-testid="footer-email"
              className="group flex items-center gap-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-ochre-400/60 px-4 py-3 transition-colors duration-200"
            >
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-ochre-500 text-white">
                <Mail className="w-4 h-4" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-xs uppercase tracking-[0.2em] text-stone2-400 font-semibold">Email</span>
                <span className="block text-base sm:text-lg font-heading font-bold text-white break-all">{CONTACT_EMAIL}</span>
              </span>
            </a>
          </div>
        </div>

        {/* Quick links */}
        <div className="md:col-span-3">
          <span className="text-xs uppercase tracking-[0.24em] text-ochre-400 font-semibold">Explore</span>
          <ul className="mt-4 space-y-2 text-sm">
            <li><Link to="/search" className="text-stone2-300 hover:text-white transition-colors duration-200">Find sitters</Link></li>
            <li><Link to="/blog" data-testid="footer-blog-link" className="text-stone2-300 hover:text-white transition-colors duration-200">Blog</Link></li>
            <li><Link to="/pricing" className="text-stone2-300 hover:text-white transition-colors duration-200">Pricing</Link></li>
            <li><Link to="/search?respite_services=traditional_respite" className="text-stone2-300 hover:text-white transition-colors duration-200">Traditional respite</Link></li>
            <li><Link to="/search?respite_services=overnight_support" className="text-stone2-300 hover:text-white transition-colors duration-200">Overnight support</Link></li>
            <li><Link to="/search?respite_services=travel_assistance" className="text-stone2-300 hover:text-white transition-colors duration-200">Travel assistance</Link></li>
            <li><Link to="/search?respite_services=party_buffers" className="text-stone2-300 hover:text-white transition-colors duration-200">Party buffers</Link></li>
            <li><Link to="/search?respite_services=community_support" className="text-stone2-300 hover:text-white transition-colors duration-200">Community support</Link></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-stone2-400">
          <span>© {new Date().getFullYear()} AutismBabysitters. All rights reserved.</span>
          <span className="flex items-center gap-1.5"><MapPin className="w-3 h-3" /> Serving families across the United States</span>
        </div>
      </div>
    </footer>
  );
}
